import { mapGetters } from "vuex"
import orderBy from "lodash/orderBy"

import is_records_list from "../../../mixins/IsRecordsList"
import project_utilities from "../../../mixins/ProjectUtilities"
import templatable from "../../../mixins/Templatable"

import ProjectsListFilterDialog from "./ProjectsListFilterDialog"
import ProjectLogsDialog from "../../../dialogs/ProjectLogsDialog"
import BaseButton from "../../../ui/BaseButton"
import OpenIcon from "../../../ui/OpenIcon"
import TemplatesControls from "../../../ui/TemplatesControls"
import TemplatePicker from "../../../ui/TemplatePicker"

var is_project = {
    mixins: [is_records_list, project_utilities, templatable],
    components: {
        ProjectsListFilterDialog,
        ProjectLogsDialog,
        BaseButton,
        OpenIcon,
        TemplatesControls,
        TemplatePicker
    },
    mounted () {
        if(typeof this.stored_state.category_ids === "undefined") {
            const new_state = {
                ...this.default_state,
                ...this.stored_state
            }
            this.set_state_values(new_state)
        }

        this.reset()
    },
    watch: {
        $route: "reset"
    },
    methods: {
        reset () {
            const new_state = {
                activated_at: !this.prospective,
                sort_by: !this.prospective ? ((this.state.status === "completed") ? "completed_at_as_string" : "sortable_number") : "days_since_received",
                sort_dir: !this.prospective ? "desc" : "asc"
            }
            this.set_state_values(new_state)
            this.refresh()
        },
        post_load (response) {
            this.state_ui.fee_min = this.fee_low
            this.state_ui.fee_max = this.fee_high
            this.state_ui.size_min = this.size_low
            this.state_ui.size_max = this.size_high

            if(!!this.$route.params.remote && (this.state.found === 1)) {
                this.$router.replace({ name: `${this.entity}.edit`, params: { id: this.current_records_ids[0] } })
            }
        },
        template_load_ui () {
            if(this.current_template && this.current_template.content) {
                this.set_state_values({ ...this.current_template.content })
                this.refresh()
            }
        },
        new_template_content () {
            return { ...this.state }
        },
        filter_by_field (value, field) {
            this.set_state_value(field, value)
            if(field === "status") {
                if(this.state.status === "completed") this.set_state_values({ sort_by: "completed_at_as_string", sort_dir: "desc" })
                else this.set_state_values({ sort_by: "sortable_number", sort_dir: "desc" })
            }
            this.refresh()
        },
        toggle_filters (show) {
            if(show) {
                this.reset_current_template()

                this.$store.dispatch("FETCH_RECORDS", { entity: "user", query: { fields: ["initials"] } }).then(() => {
                }).catch(error => {
                    this.$error(error.message)
                })
                this.$store.dispatch("FETCH_RECORDS", { entity: "category" }).then(() => {
                }).catch(error => {
                    this.$error(error.message)
                })
            }
            this.showing_filters = show
        },
        clear_filters () {
            const new_state = {
                status: "",
                studio_id: 0,
                city: "",
                state: "",
                year: "",
                pic_id: "",
                pm_id: "",
                d_id: "",
                firm_id: 0,
                fee_min: 0,
                fee_max: 0,
                size_units: "SF",
                size_min: 0,
                size_max: 0,
                category_ids: [],
                category_ids_operator: "|",
                request_types: [],
                incentives: false,
                unscheduled: false,
                consider_for_marketing: false,
                pro_bono: false
            }
            this.set_state_values(new_state)
            this.reset_current_template()
            this.refresh()
        },
        sort_rows (rows) {
            if(this.state.sort_by) {
                rows = orderBy(rows, (row) => {
                    if(!row[this.state.sort_by] && (this.state.sort_by !== "days_since_received")) {
                        if(this.state.sort_dir === "asc") return "999999999"
                        return "000000000"
                    }
                    return row[this.state.sort_by]
                }, this.state.sort_dir)
            }
            return rows
        },
        user_initials (user_id) {
            const user = this.raw_user_records.find(user => user.id === user_id)
            if(user) return user.initials
            return ""
        },
        row_classes (row) {
            return { "uk-background-danger": this.prospective && (!row.computed_starts_at_as_string || !row.computed_ends_at_as_string) }
        },
        display_logs_for_project (project) {
            this.viewing_logs_for_project = project
            this.viewing_logs = true
        },
        pick_firm (firm) {
            if(this.state.firm_id !== firm.id) {
                this.set_state_value("firm_id", firm.id)
                this.refresh()
            }
        },
        units_changed (units) {
            this.set_state_value("size_units", units)
            this.refresh()
            this.state_ui.size_min = this.size_low
            this.state_ui.size_max = this.size_high
        }
    },
    computed: {
        ...mapGetters(["entity_records"]),
        raw_studio_records () {
            return this.entity_records("studio")
        },
        raw_category_records () {
            return this.entity_records("category")
        },
        applied_filters () {
            return this.filter_description || "Filter selection of projects..."
        },
        filter_description () {
            const criteria = []

            if(this.state.status) {
                const term = this.dictionary(this.$root.constants.dictionaries.PROJECT_STATUS, null).find(term => term.code_constant === this.state.status)
                criteria.push("<strong>Status: </strong> " + (term ? term.name : this.state.status))
            }
            // if(this.state.include_dead) criteria.push('<strong>Include Dead Projects</strong>')

            if(this.state.year) criteria.push("<strong>Year: </strong> " + this.state.year)

            if(this.state.studio_id) {
                const studio = this.raw_studio_records.find(studio => studio.id === this.state.studio_id)
                criteria.push("<strong>Studio: </strong> " + (studio ? studio.name : this.state.studio_id))
            }

            if(this.state.city) criteria.push("<strong>City: </strong> " + this.state.city)
            if(this.state.state) criteria.push("<strong>State: </strong> " + this.state.state)

            if(this.state.pic_id) criteria.push("<strong>PIC: </strong> " + this.user_initials(this.state.pic_id))
            if(this.state.pm_id) criteria.push("<strong>PM: </strong> " + this.user_initials(this.state.pm_id))
            if(this.state.d_id) criteria.push("<strong>D: </strong> " + this.user_initials(this.state.d_id))

            if(this.state.firm_id) criteria.push("<strong>Firm: </strong> " + this.firm.name)

            if(!!this.state.fee_min || !!this.state.fee_max) {
                let min = this.state.fee_min
                let max = this.state.fee_max
                if(min && max) {
                    min = Math.min(this.state.fee_min, this.state.fee_max)
                    max = Math.max(this.state.fee_min, this.state.fee_max)
                }
                const fee_criteria = (range) => {
                    criteria.push("<strong>Fee: </strong> " + range)
                }
                if(!!min && !!max) {
                    fee_criteria("$" + this.nibnut_filter("nibnut.number", [min, "0,0"]) + " - $" + this.nibnut_filter("nibnut.number", [max, "0,0"]))
                } else if(min) {
                    fee_criteria("&gt;= $" + this.nibnut_filter("nibnut.number", [min, "0,0"]))
                } else if(max) {
                    fee_criteria("&lt;= $" + this.nibnut_filter("nibnut.number", [max, "0,0"]))
                }
            }
            if(!!this.state.size_min || !!this.state.size_max) {
                let min = this.state.size_min
                let max = this.state.size_max
                if(min && max) {
                    min = Math.min(this.state.size_min, this.state.size_max)
                    max = Math.max(this.state.size_min, this.state.size_max)
                }
                const size_criteria = (range) => {
                    criteria.push("<strong>Size: </strong> " + range + this.size_unit_html)
                }
                if(!!min && !!max) {
                    size_criteria(this.nibnut_filter("nibnut.number", [min, "0,0"]) + " - " + this.nibnut_filter("nibnut.number", [max, "0,0"]))
                } else if(min) {
                    size_criteria("&gt;= " + this.nibnut_filter("nibnut.number", [min, "0,0"]))
                } else if(max) {
                    size_criteria("&lt;= " + this.nibnut_filter("nibnut.number", [max, "0,0"]))
                }
            }

            if(this.state.category_ids && this.state.category_ids.length) criteria.push("<strong>Categories: </strong> " + this.category_names)

            if(this.state.request_types && this.state.request_types.length) criteria.push("<strong>RFMs: </strong> " + this.request_type_names)

            if(this.state.incentives) criteria.push("<strong>Pursuing Incentives</strong>")
            if(this.state.consider_for_marketing) criteria.push("<strong>Consider for Marketing</strong>")

            if(this.state.unscheduled) criteria.push("<strong>Without Schedule</strong>")

            if(this.state.pro_bono) criteria.push("<strong>Pro-bono</strong>")

            return criteria.join(" AND ")
        },
        available_project_status () {
            return []
        },
        size_unit_html () {
            if(this.state.size_units === "AC") return "<span>AC</span>"
            if(this.state.size_units === "SM") return "<span>M<sup>2</sup></span>"
            return "<span>Ft<sup>2</sup></span>"
        },
        category_names () {
            if(!this.raw_category_records || !this.state.category_ids || !this.state.category_ids.length) return ""

            return this.raw_category_records
                .filter(category => this.state.category_ids.indexOf(category.id) >= 0)
                .map(category => category.name).join((this.state_ui.category_ids_operator === "&") ? " & " : " | ")
        },
        export_fields () {
            const fields = [
                "name", "status_label", "shelf_life", "city_state", "client_contact_name", "client_name", "pic_initials", "pm_initials", "d_initials",
                // "project_type_label", "construction_type_label"
                "type_of_request_label",
                "project_general_type_label", "project_construction_type_label", "project_space_type_label",
                "size", "physical_address_label",
                "studio_name", "proposal_or_fee_estimate", "fee_status_date", "created_at_as_string"
            ]
            return fields.concat(this.addl_export_fields)
        }
    },
    data () {
        return {
            entity: "project",
            columns: {
                sortable_number: { label: "Number", sort: null, type: "plain" },

                name: { label: "Project Name", sort: null, type: "plain", cell_class: "lam-col-project-name" },
                city_state: { label: "Location", sort: null, type: "plain" },
                client_name: { label: "Architect", sort: false, type: "plain" },
                studio_name: { label: "Studio", sort: false, type: "plain" },
                champion_initials: { label: "Champ", sort: null, type: "plain" },
                pic_initials: { label: "PIC", sort: null, type: "plain" },
                pm_initials: { label: "PM", sort: null, type: "plain" },
                status_label: { label: "Status", sort: false, type: "plain" },
                shelf_life: { label: "Status", sort: false, type: "plain" },
                project_status: { label: "Status", sort: false, type: "plain" },
                current_state: { label: "Status", sort: false, type: "plain" },

                // received_at_as_string: { label: "Date Received", sort: null, type: "plain", head_class: "uk-text-center", cell_class: "uk-text-center" },
                computed_last_communication_at_as_string: { label: "Last Comm.", sort: null, type: "plain", head_class: "uk-text-center", cell_class: "uk-text-center" },
                days_since_received: { label: "Age", sort: null, type: "plain", head_class: "uk-text-right", cell_class: "uk-text-right" },
                computed_starts_at_as_string: { label: "Anticipated<br />Engagement", sort: null, type: "plain", head_class: "uk-text-center", cell_class: "uk-text-center" },
                computed_ends_at_as_string: { label: "Ends", sort: null, type: "plain", head_class: "uk-text-center", cell_class: "uk-text-center" },
                proposal_amount: { label: "Proposal Amount", sort: null, type: "plain" },
                proposal_sent_at_as_string: { label: "Proposal Sent", sort: null, type: "plain", head_class: "uk-text-center", cell_class: "uk-text-center" },

                active_phase: { label: "Phase", sort: false, type: "plain" },
                completed_at_as_string: { label: "Completed", sort: null, type: "plain", head_class: "uk-text-center", cell_class: "uk-text-center" },
                hours: { label: "Hours", sort: null, type: "plain", head_class: "lam-col-hours uk-text-right", cell_class: "uk-text-right" },
                computed_prospective_fee: { label: "Fee", sort: null, type: "plain", head_class: "lam-col-fee uk-text-right", cell_class: "uk-text-right" },
                computed_fee: { label: "Fee", sort: null, type: "plain", head_class: "lam-col-fee uk-text-right", cell_class: "uk-text-right" },
                computed_spent: { label: "Spent", sort: null, type: "plain", head_class: "lam-col-fee-spent uk-text-right", cell_class: "uk-text-right" },

                type_of_request_label: { label: "Type of Request", sort: false, type: "plain" },

                consider_for_marketing: { label: "Marketing", sort: false, type: "plain" },
                other_teams: { label: "Other Teams", sort: false, type: "plain" },
                internal: { label: "Internal", sort: false, type: "plain" },

                sent_at: { label: "AS Letter Sent", sort: null, type: "plain", head_class: "uk-text-center", cell_class: "uk-text-center" },
                automated_communication_log_count: { label: "Logs", sort: false, head_class: { "uk-text-center": true }, cell_class: { "uk-text-center": true } },
                recent_user_note_count: { label: "Logs", sort: false, head_class: { "uk-text-center": true }, cell_class: { "uk-text-center": true } }
            },
            default_state: {
                per_page: 30,
                page: 1,
                sort_by: "name",
                sort_dir: "asc",
                filter_on: null,
                filter: null,
                archived: false,
                activated_at: 0,
                search: "",
                total: 0,
                found: 0,

                status: "",
                include_dead: false,
                studio_id: 0,
                city: "",
                state: "",
                year: "",
                pic_id: 0,
                pm_id: 0,
                d_id: 0,
                champion_id: 0,
                firm_id: 0,
                fee_min: null,
                fee_max: null,
                size_min: null,
                size_max: null,
                size_units: "SF",
                category_ids: [],
                category_ids_operator: "|",
                request_types: [],
                incentives: false,
                consider_for_marketing: false,
                unscheduled: false,
                pro_bono: false
            },
            state_ui: {
                fee_min: null,
                fee_max: null,
                size_min: null,
                size_max: null,
                firm: { id: 0, name: "(Any firm)" }
            },

            showing_filters: false,

            viewing_logs_for_project: null,
            viewing_logs: false,

            prospective: false,
            addl_export_fields: []
        }
    }
}

export default is_project
